/**
 * Per-trader RFQ limits (V58) for the Edit Limits modal. Money fields are
 * typed in dollars and stored in cents; an empty box means "not set" =
 * unlimited (only the box ceilings apply; the key is omitted on the wire —
 * the relay rejects 0/null).
 * Relay twin: relay/src/services/rfq/traderLimits.ts.
 */
import type { TraderRules } from '../../types/shared/admin';

export type RfqLimitKey =
  | 'rfqMaxOpenLiabilityCents'
  | 'rfqMaxContractsPerQuote'
  | 'rfqDailyLossStopCents'
  | 'rfqMaxQuotesPerMin';

interface RfqLimitSpec {
  key: RfqLimitKey;
  label: string;
  help: string;
  money: boolean;
  /** Upper bound in stored units (relay RFQ_LIMIT_HARD_MAX). */
  max: number;
}

export const RFQ_LIMIT_SPECS: readonly RfqLimitSpec[] = [
  {
    key: 'rfqMaxOpenLiabilityCents',
    label: 'RFQ max open liability ($)',
    help: 'Unsettled RFQ fills plus accepted quotes awaiting confirm. Standing quotes do not count (the Board’s arm-cap rule).',
    money: true,
    max: 1_000_000_000,
  },
  {
    key: 'rfqMaxContractsPerQuote',
    label: 'RFQ max contracts per quote',
    help: 'Size of any single RFQ quote.',
    money: false,
    max: 1_000_000,
  },
  {
    key: 'rfqDailyLossStopCents',
    label: 'RFQ daily loss stop ($)',
    help: 'No new RFQ quotes once settled RFQ P&L since midnight Pacific (the Board’s day) is down this much.',
    money: true,
    max: 1_000_000_000,
  },
  {
    key: 'rfqMaxQuotesPerMin',
    label: 'RFQ max quotes per minute',
    help: 'Quotes sent in any trailing 60 seconds.',
    money: false,
    max: 100_000,
  },
];

export type RfqLimitsText = Record<RfqLimitKey, string>;
export type RfqLimitsValue = Record<RfqLimitKey, number | null>;

export function rfqLimitsTextOf(rules: TraderRules | null | undefined): RfqLimitsText {
  const out = {} as RfqLimitsText;
  for (const s of RFQ_LIMIT_SPECS) {
    const v = rules?.[s.key];
    out[s.key] = v == null ? '' : s.money ? String(v / 100) : String(v);
  }
  return out;
}

/** Parse the four boxes; returns the first bad label instead of a value. */
export function parseRfqLimitsText(text: RfqLimitsText): { ok: true; value: RfqLimitsValue } | { ok: false; label: string } {
  const value = {} as RfqLimitsValue;
  for (const s of RFQ_LIMIT_SPECS) {
    const raw = text[s.key].trim().replace(/[$,]/g, '');
    if (raw === '') {
      value[s.key] = null;
      continue;
    }
    if (s.money ? !/^\d+(\.\d{1,2})?$/.test(raw) : !/^\d+$/.test(raw)) return { ok: false, label: s.label };
    const n = s.money ? Math.round(Number(raw) * 100) : Number(raw);
    if (!Number.isSafeInteger(n) || n <= 0 || n > s.max) return { ok: false, label: s.label };
    value[s.key] = n;
  }
  return { ok: true, value };
}

export function withRfqLimits(rules: TraderRules, value: RfqLimitsValue): TraderRules {
  const next: TraderRules = { ...rules };
  for (const s of RFQ_LIMIT_SPECS) {
    const v = value[s.key];
    if (v == null) delete next[s.key];
    else next[s.key] = v;
  }
  return next;
}

export function rfqLimitsChanged(rules: TraderRules | null | undefined, value: RfqLimitsValue): boolean {
  return RFQ_LIMIT_SPECS.some((s) => (rules?.[s.key] ?? null) !== value[s.key]);
}

export function showsRfqLimits(rules: TraderRules | null | undefined): boolean {
  return rules?.rfqEnabled === true || RFQ_LIMIT_SPECS.some((s) => rules?.[s.key] != null);
}

export function RfqLimitsSection({
  value,
  onChange,
}: {
  value: RfqLimitsText;
  onChange: (next: RfqLimitsText) => void;
}) {
  return (
    <fieldset className="border-border rounded-md border px-3 py-2">
      <legend className="px-1 text-xs font-medium">RFQ limits</legend>
      <p className="text-muted-foreground mb-2 text-[11px]">
        RFQ quotes are governed only by these; the position and contract caps above don&apos;t apply to
        RFQ. Empty = unlimited (the relay&apos;s box ceilings still apply).
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {RFQ_LIMIT_SPECS.map((s) => (
          <label key={s.key} className="block text-xs">
            <span className="mb-0.5 block font-medium">{s.label}</span>
            <input
              aria-label={s.label}
              inputMode={s.money ? 'decimal' : 'numeric'}
              value={value[s.key]}
              placeholder="not set"
              onChange={(e) => onChange({ ...value, [s.key]: e.target.value })}
              className="bg-card border-border w-full rounded-md border px-2 py-1 font-mono text-xs focus:border-blue-500 focus:outline-none"
            />
            <span className="text-muted-foreground mt-0.5 block text-[10px]">{s.help}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
